import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import type { DeepAnalysis, TradingParams } from '@/types/deepAnalysis';

export type DeepAnalysisStatus = 'not_tracked' | 'data_stale' | 'insufficient_data';

export type { DeepAnalysis, TradingParams } from '@/types/deepAnalysis';
export type {
  SupportResistanceZone, TakeProfitTarget, KeyRisk,
  FundamentalDueDiligence, DDProjectFundamentals, DDTokenomics,
  DDTeamExecution, DDTechnologySecurity, DDAdoptionMetrics,
  DDMarketOpportunity, DDValuation, DDRegulatoryRisks,
  DDCommunityEcosystem, DDInvestmentFit, DDSummary,
} from '@/types/deepAnalysis';

export const useDeepAnalysis = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<DeepAnalysis | null>(null);
  const [statusMessage, setStatusMessage] = useState<{ status: DeepAnalysisStatus; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const analyzeToken = async (
    coinId: string,
    tradingParams?: TradingParams
  ): Promise<DeepAnalysis | null> => {
    setIsAnalyzing(true);
    setError(null);
    setStatusMessage(null);

    try {
      console.log('Starting analysis for:', coinId);

      // Retry transient failures (503 / network blips) with backoff before
      // surfacing "Data unavailable" to the user.
      const MAX_ATTEMPTS = 3;
      let data: any = null;
      let fnError: any = null;

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        const res = await supabase.functions.invoke('deep-analyze-token', {
          body: {
            coinId,
            tradingParams: tradingParams || {
              riskTolerance: 'Moderate',
              timeHorizon: '1-2 weeks',
              portfolioRisk: 5,
            },
          },
        });
        data = res.data;
        fnError = res.error;

        const transient =
          !!fnError &&
          !data?.rateLimited &&
          !data?.validationFailed &&
          (data?.retryable === true ||
            /50\d|timeout|network|fetch|Failed to send/i.test(fnError.message || ''));

        if (!transient || attempt === MAX_ATTEMPTS) break;
        console.warn(`deep-analyze-token transient failure (attempt ${attempt}/${MAX_ATTEMPTS}) — retrying`);
        await new Promise((r) => setTimeout(r, 600 * Math.pow(2, attempt - 1)));
      }


      // Handle function invocation errors
      if (fnError) {
        console.error('Analysis error:', fnError);

        // Check response body for structured error info
        if (data?.rateLimited) {
          const msg = data.error || 'API rate limited. Please wait and try again.';
          setError(msg);
          toast({ title: "Rate Limited", description: "Please wait 30-60 seconds and try again.", variant: "destructive" });
          return null;
        }

        if (data?.validationFailed && data?.error) {
          setError(data.error);
          toast({
            title: data.suggestions?.length > 0 ? "Token Not Found" : "Analysis Failed",
            description: data.suggestions?.length > 0
              ? `Try using: ${data.suggestions[0]}`
              : data.error.split('\n')[0],
            variant: "destructive",
          });
          return null;
        }

        if (data?.retryable) {
          setError(data.error || 'Analysis failed. Please try again.');
          toast({ title: "Temporary Error", description: "Please try again — this is usually a one-time issue.", variant: "destructive" });
          return null;
        }

        const msg = fnError.message || 'Analysis failed';
        setError(msg);
        toast({ title: "Analysis Failed", description: msg, variant: "destructive" });
        return null;
      }

      if (data?.status === 'not_tracked' || data?.status === 'data_stale' || data?.status === 'insufficient_data' || data?.verdict === 'insufficient_data') {
        const status = (data.status || 'insufficient_data') as DeepAnalysisStatus;
        const message = data.message || 'Analysis is unavailable for this token right now.';
        setAnalysis(null);
        setStatusMessage({ status, message });
        toast({ title: status === 'not_tracked' ? 'Coin Not Found' : status === 'data_stale' ? 'Data Unavailable' : 'Analysis Unavailable', description: message });
        return null;
      }

      // Handle success response with error field
      if (data?.error) {
        if (data.rateLimited) {
          setError(data.error);
          toast({ title: "Rate Limited", description: "Please wait 30-60 seconds and try again.", variant: "destructive" });
          return null;
        }
        if (data.retryable) {
          setError(data.error);
          toast({ title: "Temporary Error", description: "Please try again.", variant: "destructive" });
          return null;
        }
        setError(data.error);
        toast({ title: "Analysis Failed", description: data.error.split('\n')[0], variant: "destructive" });
        return null;
      }

      setAnalysis(data);
      toast({
        title: "Analysis Complete",
        description: `${data.tokenInfo?.name || coinId} analyzed${data.confidence ? ` with ${data.confidence}% confidence` : ''}`,
      });

      return data;
    } catch (err) {
      console.error('Unexpected error:', err);
      const msg = err instanceof Error ? err.message : 'Unexpected error occurred';
      setError(msg);
      toast({ title: "Error", description: msg, variant: "destructive" });
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  const clearAnalysis = () => {
    setAnalysis(null);
    setError(null);
    setStatusMessage(null);
  };

  return { analyzeToken, clearAnalysis, analysis, isAnalyzing, error, statusMessage };
};
